import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

// Always send cookies (auth) with every request
axios.defaults.withCredentials = true;

/**
 * Pick the API root in this order:
 *   1.  import.meta.env.VITE_API_URL   (Vite injects this at build / dev‑server time)
 *   2.  process.env.VITE_API_URL       (Node tests, storybook, etc.)
 *   3.  fallback to the old local URL in dev only
 */

axios.defaults.baseURL = process.env.API_URL || process.env.VITE_API_URL;

// Simple in-memory cache for GET requests to avoid duplicate calls during a
// single page session.  Subsequent identical requests resolve with the cached
// response instead of hitting the network again.
const responseCache = new Map<string, AxiosResponse<any>>();
const inflightRequests = new Map<string, Promise<AxiosResponse<any>>>();

// Allow consumers to clear the in-memory cache (e.g., on logout)
export const clearAxiosCache = () => {
  try {
    responseCache.clear();
    inflightRequests.clear();
  } catch {
    // no-op
  }
};

// Generate a stable cache key from URL and params
const getKey = (url: string, config?: AxiosRequestConfig) => {
  const params = config?.params ? JSON.stringify(config.params) : '';
  return `${url}?${params}`;
};

// Preserve original get method so we can wrap it with caching logic
const originalGet = axios.get.bind(axios);

(axios as any).get = function <T = any, R = AxiosResponse<T>, D = any>(
  url: string,
  config?: AxiosRequestConfig<D> & { cache?: boolean }
): Promise<R> {
  const useCache = (config as any)?.cache !== false;
  const key = getKey(url, config);

  if (useCache) {
    if (responseCache.has(key)) {
      return Promise.resolve(responseCache.get(key) as R);
    }
    if (inflightRequests.has(key)) {
      return inflightRequests.get(key) as Promise<R>;
    }
  }

  const request = originalGet<T, R, D>(url, config).then((res) => {
    if (useCache) {
      responseCache.set(key, res as AxiosResponse<any>);
    }
    return res;
  }).finally(() => {
    if (useCache) {
      inflightRequests.delete(key);
    }
  });

  if (useCache) {
    inflightRequests.set(key, request as Promise<AxiosResponse<any>>);
  }

  return request;
};

// Global handler for 401 responses (session expired)
let onUnauthorized: (() => void) | null = null;

export const setUnauthorizedHandler = (handler: () => void) => {
  onUnauthorized = handler;
};

// Add response interceptor to handle 401 responses globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && onUnauthorized) {
  // Delegate user messaging and redirects to the app layer
      onUnauthorized();
    }
    
    return Promise.reject(error);
  }
);

export default axios;
